<template>
  <div class="chat-agent-member" :class="{ active: active, disabled: !isAvailable }">
    <!-- 顶部区域：头像 + 信息 + 更多菜单 -->
    <div class="agent-top">
      <!-- 智能体头像和状态 -->
      <div class="agent-avatar">
        <div class="avatar-container">
          <head-image
            :size="48"
            :name="agent.agentName"
            :url="agent.avatarUrl"
            :style="{ borderRadius: '50%', border: '3px solid white' }"
          ></head-image>

          <!-- 多层状态指示器 -->
          <div class="status-indicators">
            <!-- 主状态（在线/离线） -->
            <div class="status-primary" :class="primaryStatusClass" :title="primaryStatusText"></div>
            <!-- 部署类型指示器 -->
            <div class="status-deployment" :class="deploymentClass" :title="deploymentText"></div>
          </div>
        </div>
      </div>

      <!-- 智能体信息 -->
      <div class="agent-info">
        <div class="agent-name" :title="agent.agentName">{{ agent.agentName }}</div>

        <div class="agent-description" :title="agent.description">
          {{ agent.description || '暂无描述' }}
        </div>

        <div class="agent-meta">
          <div class="meta-item" v-if="modeText">
            <i class="el-icon-cpu"></i>
            <span>{{ modeText }}</span>
          </div>
          <div class="meta-item" v-if="serviceStatusText">
            <i class="el-icon-connection"></i>
            <span>{{ serviceStatusText }}</span>
          </div>
          <div class="meta-item" v-if="agent.capabilities?.maxConcurrency">
            <i class="el-icon-s-data"></i>
            <span>{{ agent.capabilities.maxConcurrency }}并发</span>
          </div>
        </div>

        <!-- 技能、MCP、工作流标签 -->
        <div class="agent-tags" v-if="hasSkillsOrMcpOrWorkflow">
          <!-- 技能标签 -->
          <div class="feature-tag skills-tag" v-if="agent.skills && agent.skills.length > 0">
            <i class="el-icon-lightning"></i>
            <span class="tag-label">技能</span>
            <span class="tag-badge">{{ agent.skills.length }}</span>
          </div>

          <!-- MCP标签 -->
          <div class="feature-tag mcp-tag" v-if="agent.mcp && agent.mcp.servers && agent.mcp.servers.length > 0">
            <i class="el-icon-link"></i>
            <span class="tag-label">MCP</span>
            <span class="tag-badge">{{ agent.mcp.servers.length }}</span>
          </div>

          <!-- 工作流标签 -->
          <div class="feature-tag workflow-tag" v-if="hasWorkflow">
            <i class="el-icon-share"></i>
            <span class="tag-label">工作流</span>
            <span class="tag-badge">{{ workflowCount }}</span>
          </div>
        </div>

        <!-- 权限范围和价格信息 -->
        <div class="agent-extra-info">
          <div class="extra-info-item">
            <i class="el-icon-user"></i>
            <span class="info-label">权限:</span>
            <span class="info-value">{{ permissionScope }}</span>
          </div>
          <div class="extra-info-item price-row" @click.stop="togglePriceExpand">
            <i class="el-icon-coin"></i>
            <span class="info-label">价格:</span>
            <span class="info-value price-value">{{ priceText }}</span>
            <i
              v-if="hasPricingOptions"
              class="expand-icon"
              :class="priceExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"
            ></i>
          </div>
          <!-- 价格详情展开面板 -->
          <transition name="slide-fade">
            <div class="pricing-details" v-if="priceExpanded && hasPricingOptions">
              <div
                class="pricing-option"
                v-for="option in pricingOptions"
                :key="option.cycle"
              >
                <span class="option-cycle">{{ option.label }}</span>
                <span class="option-price">
                  <span class="currency">{{ option.currency }}</span>{{ option.amount }}<span class="unit">{{ option.unit }}</span>
                </span>
              </div>
            </div>
          </transition>
        </div>

        <!-- 不可用提示 -->
        <div class="agent-unavailable-tip" v-if="!isAvailable && unavailableReason">
          <i class="el-icon-warning"></i>
          <span>{{ unavailableReason }}</span>
        </div>

        <!-- 能力展示 -->
        <div class="agent-capabilities" v-if="displayCapabilities.length > 0">
          <el-tag
            v-for="capability in displayCapabilities"
            :key="capability"
            size="mini"
            effect="plain"
            type="info"
          >
            {{ capability }}
          </el-tag>
        </div>
      </div>

      <!-- 更多操作菜单（右上角） -->
      <div class="agent-more">
        <el-dropdown trigger="click" @command="handleCommand" placement="bottom-end">
          <el-button size="mini" type="text" @click.stop icon="el-icon-more"></el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="details">
              <i class="el-icon-view"></i> 查看详情
            </el-dropdown-item>
            <el-dropdown-item v-if="canEdit" command="edit">
              <i class="el-icon-edit"></i> 编辑配置
            </el-dropdown-item>
            <el-dropdown-item v-if="canManage" command="restart" :disabled="!isOnline">
              <i class="el-icon-refresh"></i> 重启服务
            </el-dropdown-item>
            <el-dropdown-item v-if="canEdit" command="delete" divided>
              <i class="el-icon-delete"></i> 删除
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </div>

    <!-- 底部区域：操作按钮 -->
    <div class="agent-bottom">
      <el-button
        size="small"
        plain
        @click.stop="handleCommand('share')"
      >
        <i class="el-icon-share"></i>
        <span>分享</span>
      </el-button>
      <el-button
        size="small"
        type="primary"
        :disabled="!isAvailable"
        @click.stop="$emit('start-chat', agent)"
      >
        开始对话
      </el-button>
    </div>
  </div>
</template>

<script>
import HeadImage from "../common/HeadImage.vue";

export default {
  name: "chatAgentMember",
  components: { HeadImage },
  props: {
    agent: {
      type: Object,
      required: true
    },
    active: {
      type: Boolean,
      default: false
    },
    height: {
      type: Number,
      default: 80
    },
    currentGroup: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      priceExpanded: false
    };
  },
  mounted() {
    // Agent component mounted
  },
  computed: {
    isOnline() {
      // 新API暂不支持在线状态，保留兼容性代码
      const serviceStatus = this.agent.publishInfo?.serviceStatus || this.agent.serviceStatus;
      return serviceStatus === 'online';
    },

    isAvailable() {
      // 优先使用后端返回的权限字段
      if (this.agent.canAccess !== undefined && this.agent.canAccess !== null) {
        return this.agent.canAccess;
      }

      // Fallback: 检查发布状态
      if (this.agent.publishStatus !== 'PUBLISHED') {
        return false;
      }

      // Fallback: 检查群组可见性（支持 GROUP 和 GROUP_MEMBERS）
      const accessScope = this.agent.publishSettings?.accessScope;
      if ((accessScope === 'GROUP' || accessScope === 'GROUP_MEMBERS') && this.currentGroup) {
        const selectedGroups = this.agent.publishSettings?.groupSettings?.selectedGroups || [];
        const currentGroupId = this.currentGroup.id?.toString() || this.currentGroup.groupId?.toString();

        // 如果没有指定群组（空数组），表示所有群组可见
        if (selectedGroups.length === 0) {
          return true;
        }

        // 检查当前群组是否在可见群组列表中
        return selectedGroups.some(groupId =>
          groupId?.toString() === currentGroupId
        );
      }

      // 其他情况都视为可用
      return true;
    },

    unavailableReason() {
      // 优先使用后端返回的原因
      if (this.agent.accessDeniedReason) {
        return this.agent.accessDeniedReason;
      }

      // Fallback: 未发布
      if (this.agent.publishStatus !== 'PUBLISHED') {
        return '智能体未发布';
      }

      // Fallback: 好友权限限制
      const accessScope = this.agent.publishSettings?.accessScope;
      if (accessScope === 'FRIENDS') {
        return '需要添加创作者为好友才能使用';
      }

      // Fallback: 群组权限限制（支持 GROUP 和 GROUP_MEMBERS）
      if (accessScope === 'GROUP' || accessScope === 'GROUP_MEMBERS') {
        if (!this.currentGroup) {
          return '需要加入指定群组才能使用此智能体';
        }

        const selectedGroups = this.agent.publishSettings?.groupSettings?.selectedGroups || [];
        const currentGroupId = this.currentGroup.id?.toString() || this.currentGroup.groupId?.toString();

        if (selectedGroups.length > 0) {
          const isInGroup = selectedGroups.some(groupId =>
            groupId?.toString() === currentGroupId
          );
          if (!isInGroup) {
            return '该智能体未授权在此群组使用';
          }
        }
      }

      return '';
    },
    
    isRecentlyActive() {
      if (!this.agent.lastHeartbeat) return false;
      const now = Date.now();
      const lastHeartbeat = new Date(this.agent.lastHeartbeat).getTime();
      return (now - lastHeartbeat) < 30000; // 30秒内有心跳
    },
    
    primaryStatusClass() {
      // 新API暂不支持在线状态，默认显示离线状态
      const serviceStatus = this.agent.publishInfo?.serviceStatus || this.agent.serviceStatus;
      if (serviceStatus === 'online') return 'status-online';
      if (serviceStatus === 'error') return 'status-inactive';
      return 'status-offline';
    },

    primaryStatusText() {
      // 新API暂不支持在线状态，默认显示离线文本
      const serviceStatus = this.agent.publishInfo?.serviceStatus || this.agent.serviceStatus;
      if (serviceStatus === 'online') return '在线服务';
      if (serviceStatus === 'error') return '服务异常';
      return '离线';
    },
    
    deploymentClass() {
      const type = this.agent.deployment?.type || 'unknown';
      return `deployment-${type}`;
    },
    
    deploymentText() {
      const typeMap = {
        'pc': '本地PC',
        'cloud': '云端',
        'mobile': '移动端',
        'web': 'Web端'
      };
      return typeMap[this.agent.deployment?.type] || '未知';
    },
    
    serviceStatusText() {
      // 优化：兼容新旧数据格式，显示服务状态
      const statusMap = {
        'online': '服务在线',
        'offline': '服务离线',
        'starting': '启动中',
        'error': '错误状态'
      };
      const serviceStatus = this.agent.publishInfo?.serviceStatus || this.agent.serviceStatus;
      return statusMap[serviceStatus] || null;
    },

    modeText() {
      const modeMap = {
        'architect': '架构师',
        'developer': '开发者',
        'assistant': '助手',
        'analyzer': '分析师',
        'designer': '设计师',
        'tester': '测试员',
        'code': '代码助手',
        'chat': '对话助手'
      };
      return modeMap[this.agent.mode] || null;
    },
    
    displayCapabilities() {
      const caps = [];
      if (this.agent.capabilities?.messageTypes?.includes('text')) caps.push('文本');
      if (this.agent.capabilities?.messageTypes?.includes('json')) caps.push('结构化');
      if (this.agent.capabilities?.taskTypes?.includes('execute')) caps.push('执行');
      if (this.agent.capabilities?.taskTypes?.includes('query')) caps.push('查询');
      return caps.slice(0, 3); // 最多显示3个
    },

    // 是否有技能、MCP 或工作流
    hasSkillsOrMcpOrWorkflow() {
      return (
        (this.agent.skills && this.agent.skills.length > 0) ||
        (this.agent.mcp && this.agent.mcp.servers && this.agent.mcp.servers.length > 0) ||
        this.hasWorkflow
      );
    },

    // 是否有工作流
    hasWorkflow() {
      if (!this.agent.workflow) return false;
      // 检查是否启用或者有工作流节点/workflows
      return (
        this.agent.workflow.enabled ||
        (this.agent.workflow.workflows && this.agent.workflow.workflows.length > 0) ||
        (this.agent.workflow.nodes && this.agent.workflow.nodes.length > 0)
      );
    },

    // 工作流数量
    workflowCount() {
      if (!this.agent.workflow) return 0;
      // 优先使用 workflows 数组的长度,其次使用 nodes 数组的长度
      if (this.agent.workflow.workflows && this.agent.workflow.workflows.length > 0) {
        return this.agent.workflow.workflows.length;
      }
      if (this.agent.workflow.nodes && this.agent.workflow.nodes.length > 0) {
        return this.agent.workflow.nodes.length;
      }
      return this.agent.workflow.enabled ? 1 : 0;
    },

    // 权限范围（从真实数据获取）
    permissionScope() {
      // 如果智能体未发布，显示"草稿"
      if (this.agent.publishStatus === 'DRAFT') {
        return '草稿';
      }

      // 从 publishSettings 中获取 accessScope
      const accessScope = this.agent.publishSettings?.accessScope;

      const scopeMap = {
        'PUBLIC': '公开访问',
        'PRIVATE': '仅自己',
        'FRIENDS': '好友可见',
        'GROUP': '群组可见',
        'GROUP_MEMBERS': '群组可见',
        'ORGANIZATION': '组织内部'
      };

      return scopeMap[accessScope] || '未设置';
    },

    // 价格文本（从真实数据获取）
    priceText() {
      // 如果智能体未发布，显示"未发布"
      if (this.agent.publishStatus === 'DRAFT') {
        return '未发布';
      }

      const pricing = this.agent.publishSettings?.pricing;

      if (!pricing) {
        return '未设置';
      }

      // 如果是免费的
      if (pricing.isFree) {
        return '免费';
      }

      // 货币符号
      const currency = pricing.currency || 'CNY';
      const currencySymbol = {
        'CNY': '¥',
        'USD': '$',
        'EUR': '€'
      }[currency] || currency;

      // 收集所有可用的价格选项（新格式）
      const priceOptions = [];

      if (pricing.perUsePrice && pricing.perUsePrice > 0) {
        priceOptions.push({ price: pricing.perUsePrice, label: '/次' });
      }
      if (pricing.monthlyPrice && pricing.monthlyPrice > 0) {
        priceOptions.push({ price: pricing.monthlyPrice, label: '/月' });
      }
      if (pricing.quarterlyPrice && pricing.quarterlyPrice > 0) {
        priceOptions.push({ price: pricing.quarterlyPrice, label: '/季' });
      }
      if (pricing.yearlyPrice && pricing.yearlyPrice > 0) {
        priceOptions.push({ price: pricing.yearlyPrice, label: '/年' });
      }

      // 如果新格式没有设置价格，回退到旧格式（billingCycle + price）
      if (priceOptions.length === 0 && pricing.price && pricing.price > 0) {
        const cycleLabels = {
          'PER_USE': '/次',
          'MONTHLY': '/月',
          'QUARTERLY': '/季',
          'YEARLY': '/年'
        };
        const label = cycleLabels[pricing.billingCycle] || '';
        return `${currencySymbol}${pricing.price}${label}`;
      }

      // 如果没有设置任何价格
      if (priceOptions.length === 0) {
        return '未设置';
      }

      // 显示最低价格 + "起"
      const lowestOption = priceOptions.reduce((min, opt) =>
        opt.price < min.price ? opt : min
      );

      // 如果只有一个价格选项，不显示"起"
      if (priceOptions.length === 1) {
        return `${currencySymbol}${lowestOption.price}${lowestOption.label}`;
      }

      return `${currencySymbol}${lowestOption.price}${lowestOption.label}起`;
    },

    // 是否有多个价格选项
    hasPricingOptions() {
      const pricing = this.agent.publishSettings?.pricing;
      if (!pricing || pricing.isFree) return false;
      return this.pricingOptions.length > 0;
    },

    // 获取所有价格选项
    pricingOptions() {
      const pricing = this.agent.publishSettings?.pricing;
      if (!pricing || pricing.isFree) return [];

      const currency = pricing.currency || 'CNY';
      const currencySymbol = { 'CNY': '¥', 'USD': '$', 'EUR': '€' }[currency] || currency;
      const options = [];

      // 新格式价格
      if (pricing.perUsePrice && pricing.perUsePrice > 0) {
        options.push({
          cycle: 'PER_USE',
          label: '按次',
          price: pricing.perUsePrice,
          currency: currencySymbol,
          amount: pricing.perUsePrice,
          unit: '/次',
          priceText: `${currencySymbol}${pricing.perUsePrice}/次`
        });
      }
      if (pricing.monthlyPrice && pricing.monthlyPrice > 0) {
        options.push({
          cycle: 'MONTHLY',
          label: '按月',
          price: pricing.monthlyPrice,
          currency: currencySymbol,
          amount: pricing.monthlyPrice,
          unit: '/月',
          priceText: `${currencySymbol}${pricing.monthlyPrice}/月`
        });
      }
      if (pricing.quarterlyPrice && pricing.quarterlyPrice > 0) {
        options.push({
          cycle: 'QUARTERLY',
          label: '按季',
          price: pricing.quarterlyPrice,
          currency: currencySymbol,
          amount: pricing.quarterlyPrice,
          unit: '/季',
          priceText: `${currencySymbol}${pricing.quarterlyPrice}/季`
        });
      }
      if (pricing.yearlyPrice && pricing.yearlyPrice > 0) {
        options.push({
          cycle: 'YEARLY',
          label: '按年',
          price: pricing.yearlyPrice,
          currency: currencySymbol,
          amount: pricing.yearlyPrice,
          unit: '/年',
          priceText: `${currencySymbol}${pricing.yearlyPrice}/年`
        });
      }

      // 如果新格式没有，回退到旧格式
      if (options.length === 0 && pricing.price && pricing.price > 0) {
        const cycleLabels = {
          'PER_USE': '按次',
          'MONTHLY': '按月',
          'QUARTERLY': '按季',
          'YEARLY': '按年'
        };
        const cycleSuffix = {
          'PER_USE': '/次',
          'MONTHLY': '/月',
          'QUARTERLY': '/季',
          'YEARLY': '/年'
        };
        const unit = cycleSuffix[pricing.billingCycle] || '/月';
        options.push({
          cycle: pricing.billingCycle || 'MONTHLY',
          label: cycleLabels[pricing.billingCycle] || '按月',
          price: pricing.price,
          currency: currencySymbol,
          amount: pricing.price,
          unit: unit,
          priceText: `${currencySymbol}${pricing.price}${unit}`
        });
      }

      return options;
    },

    canEdit() {
      return this.agent.userId === this.currentUserId;
    },
    
    canManage() {
      return this.canEdit && this.agent.deployment?.type === 'pc';
    },
    
    canShare() {
      // 任何人都可以分享智能体，不限于自己的智能体
      return true;
    },
    
    currentUserId() {
      return this.$store.state.userStore.userInfo.id?.toString();
    }
  },
  methods: {
    startChat() {
      // 新API返回的智能体都是可用的，直接触发
      this.$emit('start-chat', this.agent);
    },

    handleCommand(command) {
      this.$emit('action', { command, agent: this.agent });
    },

    togglePriceExpand() {
      if (this.hasPricingOptions) {
        this.priceExpanded = !this.priceExpanded;
      }
    }
  }
}
</script>

<style scoped lang="scss">
.chat-agent-member {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 12px;
  transition: all 0.3s ease;
  border-radius: 12px;
  border: 2px solid transparent;
  background: linear-gradient(white, white) padding-box,
              linear-gradient(45deg, #f0f2f5, #e6e8ec) border-box;
  margin-bottom: 12px;

  &:hover {
    background: linear-gradient(#f8fafb, #f8fafb) padding-box,
                linear-gradient(45deg, #1890ff, #36cfc9) border-box;
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.12);
  }

  &.active {
    background: linear-gradient(#e6f7ff, #e6f7ff) padding-box,
                linear-gradient(45deg, #1890ff, #40a9ff) border-box;
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
  }

  &.disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  .agent-top {
    display: flex;
    align-items: flex-start;
    margin-bottom: 12px;

    .agent-avatar {
      margin-right: 16px;
      flex-shrink: 0;

      .avatar-container {
        position: relative;
        width: 52px;
        height: 52px;

        .status-indicators {
          position: absolute;
          bottom: -2px;
          right: -2px;

          .status-primary {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            border: 3px solid white;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
            background: linear-gradient(45deg, #52c41a, #73d13d);
          }
        }
      }
    }

    .agent-info {
      flex: 1;
      min-width: 0;

      .agent-name {
        font-weight: 600;
        font-size: 16px;
        color: #262626;
        word-wrap: break-word;
        word-break: break-word;
        line-height: 1.4;
        margin-bottom: 8px;
        text-align: left;
      }

      .agent-description {
        font-size: 13px;
        color: #595959;
        line-height: 1.4;
        margin-bottom: 12px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        word-break: break-word;
        text-align: left;
      }

      .agent-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-bottom: 8px;

        .meta-item {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #8c8c8c;

          i {
            font-size: 14px;
            color: #bfbfbf;
          }
        }
      }

      // 技能、MCP、工作流标签
      .agent-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 8px;
        margin-bottom: 8px;

        .feature-tag {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          transition: all 0.2s ease;

          i {
            font-size: 12px;
          }

          .tag-label {
            line-height: 1;
          }

          .tag-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 16px;
            height: 16px;
            padding: 0 5px;
            border-radius: 8px;
            font-size: 10px;
            font-weight: 500;
            line-height: 1;
          }

          // 技能标签 - 蓝色
          &.skills-tag {
            background-color: rgba(64, 158, 255, 0.12);
            color: #409EFF;

            .tag-badge {
              background-color: rgba(64, 158, 255, 0.25);
              color: #409EFF;
            }

            &:hover {
              background-color: rgba(64, 158, 255, 0.2);
            }
          }

          // MCP标签 - 橙色
          &.mcp-tag {
            background-color: rgba(255, 149, 0, 0.12);
            color: #FF9500;

            .tag-badge {
              background-color: rgba(255, 149, 0, 0.25);
              color: #FF9500;
            }

            &:hover {
              background-color: rgba(255, 149, 0, 0.2);
            }
          }

          // 工作流标签 - 绿色
          &.workflow-tag {
            background-color: rgba(103, 194, 58, 0.12);
            color: #67C23A;

            .tag-badge {
              background-color: rgba(103, 194, 58, 0.25);
              color: #67C23A;
            }

            &:hover {
              background-color: rgba(103, 194, 58, 0.2);
            }
          }
        }
      }

      // 权限范围和价格信息
      .agent-extra-info {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-top: 8px;
        margin-bottom: 8px;
        padding: 8px 0;

        .extra-info-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #595959;

          i {
            font-size: 14px;
            color: #8c8c8c;
            flex-shrink: 0;
          }

          .info-label {
            color: #8c8c8c;
            font-weight: 500;
            flex-shrink: 0;
          }

          .info-value {
            color: #262626;
            font-weight: 500;

            &.price-value {
              color: #262626;
              font-weight: 600;
              font-size: 14px;
            }
          }

          &.price-row {
            cursor: pointer;

            .expand-icon {
              margin-left: 4px;
              font-size: 10px;
              color: #bfbfbf;
            }
          }
        }

        // 价格详情展开面板 - 微信支付风格
        .pricing-details {
          display: flex;
          flex-direction: column;
          gap: 0;
          margin-top: 4px;
          margin-left: 20px;
          padding-left: 12px;
          border-left: 2px solid #f0f0f0;
          user-select: none;

          .pricing-option {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            padding: 6px 0;
            font-size: 13px;
            cursor: default;

            .option-cycle {
              color: #8c8c8c;
              font-weight: 400;
            }

            .option-price {
              color: #262626;
              font-weight: 500;
              font-size: 14px;

              .currency {
                font-size: 12px;
                font-weight: 400;
                margin-right: 1px;
              }

              .unit {
                font-size: 11px;
                font-weight: 400;
                color: #8c8c8c;
                margin-left: 1px;
              }
            }
          }
        }

        // 展开动画
        .slide-fade-enter-active {
          transition: all 0.2s ease-out;
        }
        .slide-fade-leave-active {
          transition: all 0.15s ease-in;
        }
        .slide-fade-enter-from,
        .slide-fade-leave-to {
          opacity: 0;
          transform: translateY(-6px);
        }
      }

      // 不可用提示
      .agent-unavailable-tip {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 12px;
        margin-top: 8px;
        background-color: #fff7e6;
        border-left: 3px solid #faad14;
        border-radius: 4px;
        font-size: 12px;
        color: #d46b08;

        i {
          font-size: 14px;
          color: #faad14;
          flex-shrink: 0;
        }

        span {
          line-height: 1.5;
        }
      }

      .agent-capabilities {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;

        .el-tag {
          font-size: 11px;
          height: 20px;
          line-height: 18px;
          border-radius: 10px;
        }
      }
    }

    .agent-more {
      flex-shrink: 0;
      margin-left: 8px;
    }
  }

  .agent-bottom {
    display: flex;
    justify-content: flex-end;
    gap: 8px;

    .el-button {
      i {
        margin-right: 4px;
      }
    }

    .el-button--primary {
      background: linear-gradient(45deg, #1890ff, #40a9ff);
      border: none;
      box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);
      cursor: pointer;

      &:hover {
        transform: scale(1.05);
        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.4);
      }

      &:disabled {
        cursor: not-allowed;
      }
    }
  }
}
</style>